import React, { createContext, useCallback, useContext, useMemo } from "react";
import axiosInstance from "../utils/axiosConfig";

const Vision360Context = createContext(null);

const BASE_360 = (process.env.REACT_APP_BACKEND_URL_MS_360 || "https://api.unfrauded.com/api/vision360").replace(/\/+$/, "");

const clientsCache = new Map();
const carsCache = new Map();
const pendingClients = new Map();
const pendingCars = new Map();

const requestClient = async (partyUid) => {
  const url = `${BASE_360}/client/${encodeURIComponent(partyUid)}`;
  const response = await axiosInstance.get(url);
  return response?.data || null;
};

const requestCar = async (carUid) => {
  const url = `${BASE_360}/cars/car/${encodeURIComponent(carUid)}`;
  const response = await axiosInstance.get(url);
  return response?.data || null;
};

export const Vision360Provider = ({ children }) => {
  const getClient = useCallback((partyUid) => {
    if (!partyUid) return null;
    return clientsCache.get(partyUid) || null;
  }, []);

  const getCar = useCallback((carUid) => {
    if (!carUid) return null;
    return carsCache.get(carUid) || null;
  }, []);

  const fetchClient = useCallback(async (partyUid) => {
    if (!partyUid) return null;
    const existing = clientsCache.get(partyUid);
    if (existing) return existing;
    if (pendingClients.has(partyUid)) {
      return pendingClients.get(partyUid);
    }
    const promise = requestClient(partyUid)
      .then((data) => {
        if (data) {
          clientsCache.set(partyUid, data);
        }
        return data;
      })
      .finally(() => {
        pendingClients.delete(partyUid);
      });
    pendingClients.set(partyUid, promise);
    return promise;
  }, []);

  const fetchCar = useCallback(async (carUid) => {
    if (!carUid) return null;
    const existing = carsCache.get(carUid);
    if (existing) return existing;
    if (pendingCars.has(carUid)) {
      return pendingCars.get(carUid);
    }
    const promise = requestCar(carUid)
      .then((data) => {
        if (data) {
          carsCache.set(carUid, data);
        }
        return data;
      })
      .finally(() => {
        pendingCars.delete(carUid);
      });
    pendingCars.set(carUid, promise);
    return promise;
  }, []);

  const fetchFirstAvailableCar = useCallback(
    async (carUids = []) => {
      const uniqueUids = Array.from(new Set((carUids || []).filter(Boolean)));
      for (const uid of uniqueUids) {
        try {
          const data = await fetchCar(uid);
          if (data?.car) {
            return { carUid: uid, data };
          }
        } catch (error) {
          console.warn("[Vision360] Unable to fetch car", uid, error);
        }
      }
      return { carUid: null, data: null };
    },
    [fetchCar]
  );

  const prefetchVisionData = useCallback(
    async ({ clientUid, carUids = [] }) => {
      const tasks = [];
      if (clientUid) {
        tasks.push(fetchClient(clientUid));
      }
      const carPromises = [];
      // Ne précharger que le premier véhicule : chaque appel car déclenche une
      // agrégation complète côté gateway, les suivants se chargent à la demande
      const uniqueCarUids = Array.from(new Set((carUids || []).filter(Boolean))).slice(0, 1);
      uniqueCarUids.forEach((uid) => {
        carPromises.push(fetchCar(uid));
      });
      tasks.push(...carPromises);
      if (tasks.length === 0) return;
      try {
        await Promise.all(tasks);
      } catch (error) {
        console.warn("[Vision360] Prefetch warning", error);
      }
    },
    [fetchClient, fetchCar]
  );

  const value = useMemo(
    () => ({
      getClient,
      getCar,
      fetchClient,
      fetchCar,
      fetchFirstAvailableCar,
      prefetchVisionData,
    }),
    [getClient, getCar, fetchClient, fetchCar, fetchFirstAvailableCar, prefetchVisionData]
  );

  return <Vision360Context.Provider value={value}>{children}</Vision360Context.Provider>;
};

export const useVision360Cache = () => {
  const ctx = useContext(Vision360Context);
  if (!ctx) {
    throw new Error("useVision360Cache must be used within a Vision360Provider");
  }
  return ctx;
};
